/** ~380 DPI across an ID-1 card at 80% frame fill — 25% above the proven 1280px webcam path. */
export const DEFAULT_LONG_EDGE = 1600;

/** Matches the webcam capture path's `jpeg_quality: 90`. */
const DEFAULT_QUALITY = 0.9;

/** Re-encode anything over this even when it already fits the long edge — uploads stay small. */
export const DEFAULT_MAX_BYTES = 2_000_000;

/** A truncated JPEG does not throw this. Its bytes are still storable. */
export class UndecodableImageError extends Error {
  constructor() {
    super('the browser cannot decode this image');
    this.name = 'UndecodableImageError';
  }
}

type ResizeOptions = {
  longEdge?: number;
  quality?: number;
  /** Byte size above which a file is re-encoded even when its dimensions already fit. */
  maxBytes?: number;
};

/**
 * @returns a JPEG or PNG File, with a `type` that matches its bytes.
 * @throws UndecodableImageError when the pick is neither a stored format nor decodable.
 */
export default async function resizeImage(file: File, options: ResizeOptions = {}): Promise<File> {
  const longEdge = options.longEdge ?? DEFAULT_LONG_EDGE;
  const quality = options.quality ?? DEFAULT_QUALITY;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;

  // The bytes decide, never file.type. The browser reads the type off the file name.
  const stored = storedType(await head(file));

  const bitmap = await decode(file);
  if (!bitmap) {
    if (stored) return retype(file, stored);
    throw new UndecodableImageError();
  }

  try {
    const scale = Math.min(1, longEdge / Math.max(bitmap.width, bitmap.height));
    if (stored && scale === 1 && file.size <= maxBytes) return retype(file, stored);

    const jpeg = await toJpeg(bitmap, scale, quality);
    if (!jpeg) {
      if (stored) return retype(file, stored);
      throw new UndecodableImageError();
    }

    return new File([jpeg], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } finally {
    bitmap.close();
  }
}

async function decode(file: File): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== 'function') return null;

  // 'from-image' bakes in EXIF rotation; some Safari versions reject the options argument.
  const attempts: (ImageBitmapOptions | undefined)[] = [{ imageOrientation: 'from-image' }, undefined];

  for (const options of attempts) {
    try {
      return await createImageBitmap(file, options);
    } catch {
      continue;
    }
  }

  return null;
}

async function toJpeg(bitmap: ImageBitmap, scale: number, quality: number): Promise<Blob | null> {
  try {
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return null;

    // JPEG flattens alpha onto black. Fill white so transparency does not darken the image.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return null;

    // toBlob may ignore the requested type.
    return storedType(await head(blob)) === 'image/jpeg' ? blob : null;
  } catch {
    return null;
  }
}

async function head(blob: Blob): Promise<Uint8Array> {
  try {
    return new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  } catch {
    return new Uint8Array(0);
  }
}

/** null means the API will not store these bytes, not that the format is unknown. */
function storedType(bytes: Uint8Array): string | null {
  if (startsWith(bytes, 0xff, 0xd8)) return 'image/jpeg';
  if (startsWith(bytes, 0x89, 0x50, 0x4e, 0x47)) return 'image/png';

  return null;
}

function startsWith(bytes: Uint8Array, ...expected: number[]): boolean {
  if (bytes.length < expected.length) return false;

  return expected.every((byte, index) => bytes[index] === byte);
}

/** The caller's gate reads file.type, which the file name may have got wrong. */
function retype(file: File, type: string): File {
  if (file.type === type) return file;

  return new File([file], file.name, { type, lastModified: file.lastModified });
}

function jpegName(name: string): string {
  return `${name.replace(/\.[^.]+$/, '')}.jpg`;
}
